<template>
    <setting-layout :title="$t('pages.user.setting.creator.verify_ondato.title')">
        <v-container class="pt-2">
            <!-- User Verification Block -->
            <v-flex v-if="user.verifyOndatoAt">
                <h2 class="text--secondary">
                    {{ $t('pages.user.setting.creator.verify_ondato.verify_account') }}:
                </h2>
                <div>
                    <v-icon v-if="!userCanReverify" class="success-icon">mdi-checkbox-marked-circle</v-icon>
                    <v-icon v-else color="red">mdi-alert-circle-outline</v-icon>
                    {{ successMessage }}
                    <v-btn
                        v-if="userCanReverify"
                        :loading="loading.verificationUser"
                        @click="toVerificationUserLink"
                        small
                        class="ml-2 gradient-color">
                        {{ $t('pages.user.setting.creator.verify_ondato.reverify_button') }}
                    </v-btn>
                </div>
            </v-flex>

            <div v-else class="text-center">
                {{ statusMessage(verificationUser) }}
                <gradient-button
                    :loading="loading.verificationUser"
                    @click="toVerificationUserLink"
                    class="ma-2 mt-3">
                    {{ $t('pages.user.setting.creator.verify_ondato.title') }}
                </gradient-button>
            </div>

            <div class="AnotherModels-wrapper" v-if="showAnotherModels">
                <div v-for="(item, key) in filteredAnotherModels" :key="key" class="AnotherModels-item">
                    <div v-if="item.verify_at === null" class="d-flex justify-space-between">
                        <div>
                            <v-icon color="red">mdi-alert-circle-outline</v-icon>
                            {{ $t('pages.user.setting.creator.verify_ondato.model_verification_expired', { name: `${item.firstname} ${item.lastname}` }) }}
                            <v-btn
                                :loading="item.loading"
                                small
                                @click="reverifyAnotherModel(item.id)"
                                class="ml-2 gradient-color">
                                {{ $t('pages.user.setting.creator.verify_ondato.reverify_button') }}
                            </v-btn>
                            <v-btn
                                :loading="item.loading"
                                small
                                color="red"
                                @click="removeAnotherModel(item.id)"
                                class="ml-2">
                                {{ $t('pages.user.setting.creator.verify_ondato.remove_verification') }}
                            </v-btn>
                        </div>
                    </div>
                    <div v-else>
                        <div>
                            <v-icon v-if="!isModelExpiring(item)" class="success-icon">mdi-checkbox-marked-circle</v-icon>
                            <v-icon v-else color="red">mdi-alert-circle-outline</v-icon>

                            {{
                                $tc('pages.user.setting.creator.verify_ondato.model_has_verified', item.id, {name: `${item.firstname} ${item.lastname}`})
                            }}
                            {{ moment(item.verify_at).format('L') }}

                            <!-- Re-verify another model if expired -->
                            <v-btn
                                v-if="isModelExpiring(item)"
                                :loading="item.loading"
                                small
                                @click="reverifyAnotherModel(item.id)"
                                class="ml-2 gradient-color">
                                {{ $t('pages.user.setting.creator.verify_ondato.reverify_button') }}
                            </v-btn>
                            <v-btn
                                v-if="isModelExpiring(item)"
                                :loading="item.loading"
                                small
                                color="red"
                                @click="removeAnotherModel(item.id)"
                                class="ml-2">
                                {{ $t('pages.user.setting.creator.verify_ondato.remove_verification') }}
                            </v-btn>
                        </div>
                    </div>
                </div>
                <div class="AnotherModels-buttons" v-if="!hideAddModelsButton">
                    <gradient-button @click="toVerificationAnotherModelLink" :loading="loading.verificationAnotherModel" class="ma-2">
                        {{ $t('pages.user.setting.creator.verify_ondato.add_another_model') }}
                    </gradient-button>
                </div>
            </div>
        </v-container>
    </setting-layout>
</template>

<script>
import SettingLayout from "@/layouts/app/profile/SettingLayout"
import {mapActions, mapState, mapMutations} from "vuex"

import moment from "moment";
import GradientButton from "@/components/app/button/GradientButton";

const STATUS_CREATED = 1;
const STATUS_PROCESSED = 2;
const STATUS_RESOLVED = 3;
const STATUS_REJECTED = 4;

export default {
    name: "VerifyOndato",
    components: {
        GradientButton,
        SettingLayout,
    },
    data() {
        return {
            loading: {
                verificationUser: false,
                verificationAnotherModel: false,
            },
            anotherModels: [],
            verificationUser: {status: null, date: null},
        }
    },
    computed: {
        ...mapState({
            fetchedAnotherModels: state => state.anotherModels.anotherModels,
            user: state => state.user.user.data
        }),
        status() {
            return {
                [STATUS_CREATED]: this.$tc('pages.user.setting.creator.verify_ondato.status_created'),
                [STATUS_PROCESSED]: this.$tc('pages.user.setting.creator.verify_ondato.status_processed'),
                [STATUS_RESOLVED]: this.$tc('pages.user.setting.creator.verify_ondato.status_resolved'),
                [STATUS_REJECTED]: this.$tc('pages.user.setting.creator.verify_ondato.status_rejected'),
            }
        },
        showAnotherModels() {
            return !!this.user.verifyOndatoAt
        },
        successMessage() {
            const firstname = this.user.firstname || '';
            const lastname = this.user.lastname || '';
            const fullName = `${firstname} ${lastname}`.trim();
            return this.$tc('pages.user.setting.creator.verify_ondato.model_has_verified', this.user.name, {name: fullName}) + " " + moment(this.user.verifyOndatoAt).format('L')
        },
        userCanReverify() {
            return this.user.can_reverify_ondato_document;
        },
        hideAddModelsButton() {
            const canUserReverify = this.user.can_reverify_ondato_document;
            const anyModelCanReverify = this.filteredAnotherModels.some(
                model => model.can_reverify_ondato_document
            );

            return canUserReverify || anyModelCanReverify;
        },
        filteredAnotherModels() {
            return this.anotherModels.filter(model => {
                return model.verify_at || (model.ondato && model.ondato.status == STATUS_RESOLVED);
            });
        },
    },
    watch: {
        async user() {
            if (this.user.id === undefined) {
                return false
            }
            this.verificationUser = await this.getStatusVerificationUser(this.user.id)
        },
        async fetchedAnotherModels() {
            this.anotherModels = [...this.fetchedAnotherModels]
        },
    },
    methods: {
        ...mapMutations(["showSnackBar"]),
        ...mapActions([
            'getAnotherModels',
            'addAnotherModel',
            'getCurrentUserData',
        ]),
        async getStatusVerificationUser(id) {
            return await window.axios
                .get(`/api/ondato/status-verification-user/${id}`, { hideLoader: true })
                .then(({data, status}) => status === 200 ? data : null)
                .catch(() => null)
        },
        toVerificationUserLink() {
            this.loading.verificationUser = true
            return window.axios('api/ondato/verification-user-link')
                .then(({data}) => window.location.href = data.redirectUrl)
                .catch(e => {
                    this.showSnackBar(e.response?.data?.message || 'Something went wrong.');
                })
                .finally(() => this.loading.verificationUser = false)
        },
        toVerificationAnotherModelLink() {
            this.loading.verificationAnotherModel = true;

            return window.axios(`api/ondato/verification-another-model-link`)
                .then(({data}) => window.location.href = data.redirectUrl)
                .catch(e => {
                    if (e.response) {
                        this.showSnackBar(e.response.data.message || 'Something went wrong.');
                    } else {
                        this.showSnackBar('Something went wrong.');
                    }
                })
                .finally(() => {
                    this.loading.verificationAnotherModel = false;
                })
        },
        statusMessageAnotherModel({id, ondato}) {
            const message = this.$tc('pages.user.setting.creator.verify_ondato.model_not_verified', id, {name: id})
            if (!ondato) {
                return message
            }
            return `${message}. ${this.statusMessage(ondato)}`
        },
        statusMessage({status, date}) {
            if (!status || status === STATUS_CREATED) {
                return ''
            }
            return moment(date).format('L') + ' : ' + this.status[status]
        },
        isShowAnotherModelLink({ondato}) {
            return !ondato.status || ondato.status === STATUS_CREATED || ondato.status === STATUS_REJECTED
        },
        async handleAddAnotherModel() {
            this.addAnotherModel()
                .then(() => {
                    this.getAnotherModels()
                })
        },

        isModelExpiring(model) {
            return model.can_reverify_ondato_document;
        },
        reverifyAnotherModel(modelId) {
            axios.post(`/api/ondato/another-model/${modelId}/reverify`)
                .then(({ data }) => {
                    window.location.href = data.redirectUrl;
                })
                .catch(e => {
                    this.showSnackBar(e.response.data.message || 'Something went wrong.');
                })
        },
        removeAnotherModel(modelId) {
            axios.post(`/api/ondato/another-model/${modelId}/remove`)
                .then((res) => {
                    this.getAnotherModels();
                    this.showSnackBar(res.message || 'Model removed.');
                    this.getCurrentUserData();
                })
                .catch(error => {
                    this.showSnackBar('Something went wrong.');
                })
        },
    },
    created() {
        this.getAnotherModels()
        this.getCurrentUserData()
    }
}
</script>

<style lang="scss" scoped>
@import '@/sass/modules/_variables';

.success-icon {
    color: map-get($green, 'lighten-1');
    background-color: white;
    width: 20px;
    height: 20px;
    border-radius: 40px;
    margin-right: 5px;
}

.AnotherModels {
    &-wrapper {
        display: flex;
        flex-wrap: wrap;
        flex-direction: column;
        margin-top: 1em;
    }

    &-item {
        margin-bottom: 1em;
    }

    &-buttons {
        display: flex;
        justify-content: center;

        button {
            max-width: 320px;
            width: 100%;
        }
    }
}

.gradient-color {
    background: $accent-background;
}
</style>
